import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { DEFAULT_SCROLL_SIZE } from '../config/base-table.config';
import { ScrollManager } from './scroll-manager.service';
import { ObjectApiInterface } from '../base/interfaces/object-api.interface';
import { NavigationEnd, Router } from '@angular/router';

interface AutocompleteState<T> {
  subject: BehaviorSubject<T[]>;
  paginationManager: ScrollManager;
  currentPage: number;
  lastSearch: string;
}

@Injectable({
  providedIn: 'root',
})
export class AutocompleteService {
  states: { [key: string]: AutocompleteState<any> } = {};
  private lastPath: string | null = null;

  constructor(private router: Router) {
    // Nettoyer les états au changement de page uniquement : une navigation qui ne change que les query params
    // (synchronisation des filtres dans l'URL, PRED-3054) laisse le sidenav de filtres en vie et ne doit pas vider ses listes.
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(event => {
        const path = event.urlAfterRedirects.split('?')[0];
        if (this.lastPath !== null && path !== this.lastPath) {
          this.clearStates();
        }
        this.lastPath = path;
      });
  }

  private initializeStateIfNeeded<T>(filterName: string): void {
    if (filterName !== '' && !this.states[filterName]) {
      this.states[filterName] = {
        subject: new BehaviorSubject<T[]>([]),
        paginationManager: new ScrollManager(),
        currentPage: 1,
        lastSearch: '',
      };
    }
  }

  clearStates(): void {
    Object.values(this.states).forEach(state => {
      if (state.subject instanceof BehaviorSubject) {
        state.subject.next([]);
      }
    });
    this.states = {};
  }

  /**
   * Injecte des items (ex. chips restaurées depuis l'URL) dans l'état d'un autocomplete, marqués `selected`
   * pour que la fusion du scroll infini ne les écrase pas (PRED-3054).
   */
  upsertItems<T extends Record<string, any>>(filterName: string, items: T[], property = '_id'): void {
    this.initializeStateIfNeeded<T>(filterName);
    const state = this.states[filterName];
    const byKey = new Map<unknown, T>((state.subject.value ?? []).map(item => [item[property], item]));
    items.forEach(item => byKey.set(item[property], { ...item, selected: true }));
    state.subject.next(Array.from(byKey.values()));
  }

  getItems$<T>(filterName: string): Observable<T[]> {
    this.initializeStateIfNeeded<T>(filterName);
    return this.states[filterName].subject.asObservable();
  }

  resetItem<T>(filterName: string): void {
    if (this.states[filterName]) {
      const state = this.states[filterName];
      state.subject.next([]);
      state.paginationManager.reset();
      state.currentPage = 1;
      state.lastSearch = '';
    }
  }

  autocompleteInfiniteScroll<T>(
    search: string,
    filterName: string,
    property: string = '_id',
    pageToGo: 'next' | 'previous',
    serviceFn: (page: number, size: number, filterName: string, search: string) => Observable<ObjectApiInterface<T[]>>,
  ): Observable<T[]> {
    this.initializeStateIfNeeded<T>(filterName);

    const state = this.states[filterName];

    if (!state) {
      return of([]);
    }

    if (!pageToGo && search !== state.lastSearch) {
      state.paginationManager.reset();
      state.currentPage = 1;
      state.lastSearch = search;
    }

    if (!!pageToGo && pageToGo === 'next') {
      const nextPage = state.paginationManager.getNextPage();
      if (nextPage === null) {
        return of([]);
      }
      state.currentPage = nextPage;
    }

    if (state.paginationManager.isPageLoaded(state.currentPage)) {
      return of([]);
    }

    return serviceFn(state.currentPage, DEFAULT_SCROLL_SIZE, filterName, search).pipe(
      map((response: any) => {
        state.paginationManager.setMaxPages(response.totalPage);
        return response.data;
      }),
      map(newData => {
        const existingData = state.subject.value || [];
        const existingIndex = new Map(existingData.map(item => [item[property], item]));

        for (const item of newData) {
          const key = item[property];
          const existingItem = existingIndex.get(key);

          if (!existingItem) {
            existingIndex.set(key, item);
          } else if (!existingItem.selected) {
            existingIndex.set(key, item);
          }
        }

        const uniqueData = Array.from(existingIndex.values());
        state.subject.next(uniqueData);
        state.paginationManager.markPageLoaded(state.currentPage);
        return uniqueData;
      }),
    );
  }

  autocompleteInfiniteScrollNeeds<T>(
    search: string,
    filterName: string,
    property: string = '_id',
    pageToGo: 'next' | 'previous',
    serviceFn: (page: number, size: number, filterName: string, search: string) => Observable<ObjectApiInterface<T[]>>,
  ): Observable<T[]> {
    const needsFilterName = `needs-${filterName}`;
    return this.autocompleteInfiniteScroll(search, needsFilterName, property, pageToGo, serviceFn);
  }
}
